import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Truck, CheckCircle2 } from 'lucide-react';
import { useSupplierAuth } from '../../lib/supplierAuthStore';
import { PoweredByRTA } from '../../components/PoweredByRTA';
export function SupplierLogin() {
  const navigate = useNavigate();
  const {
    signIn
  } = useSupplierAuth();
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
  const phoneValid = phone.replace(/\D/g, '').length >= 9;
  const handleSendOtp = (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneValid) {
      setError('Enter a valid UAE phone number');
      return;
    }
    setError('');
    setStep('otp');
    setTimeout(() => inputsRef.current[0]?.focus(), 60);
  };
  const updateOtp = (idx: number, value: string) => {
    const v = value.replace(/\D/g, '').slice(0, 1);
    setOtp(arr => {
      const next = [...arr];
      next[idx] = v;
      return next;
    });
    if (v && idx < 5) inputsRef.current[idx + 1]?.focus();
  };
  const onOtpKey = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) inputsRef.current[idx - 1]?.focus();
  };
  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.some(d => d === '')) {
      setError('Enter the full 6-digit code');
      return;
    }
    setError('');
    signIn(`+971 ${phone}`);
    navigate('/supplier/dashboard');
  };
  return <div className="min-h-screen flex bg-white" data-id="element-3396">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex w-1/2 bg-gray-900 relative overflow-hidden flex-col justify-between p-12" data-id="element-3397">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1580674285054-bed31e145f59?auto=format&fit=crop&q=80&w=1600')] bg-cover bg-center opacity-20 mix-blend-luminosity" data-id="element-3398" />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent" data-id="element-3399" />

        <div className="relative z-10" data-id="element-3400">
          <div className="flex items-center justify-between gap-4 mb-12" data-id="element-3401">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Back to modules" data-id="element-3402">
              <img src="https://cdn.magicpatterns.com/uploads/kwTMidwDpg46YyVPyEcdAS/Pair2_(2).svg" alt="Pair2" className="h-9 w-auto brightness-0 invert" data-id="element-3403" />
              <span className="font-display font-bold text-2xl text-white/90" data-id="element-3404">
                Carrier
              </span>
            </Link>
            <PoweredByRTA variant="dark" data-id="element-3405" />
          </div>
        </div>

        <div className="relative z-10 max-w-md" data-id="element-3406">
          <h1 className="text-4xl font-display font-bold text-white mb-6 leading-tight" data-id="element-3407">
            Grow your fleet's revenue with AI-driven dispatch.
          </h1>
          <div className="space-y-4" data-id="element-3408">
            <div className="flex items-center gap-3 text-gray-300" data-id="element-3409">
              <CheckCircle2 className="w-5 h-5 text-brand-500" data-id="element-3410" />
              <span data-id="element-3411">Trusted by 1,200+ UAE carriers</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300" data-id="element-3412">
              <CheckCircle2 className="w-5 h-5 text-brand-500" data-id="element-3413" />
              <span data-id="element-3414">12K+ jobs delivered last month</span>
            </div>
            <div className="flex items-center gap-3 text-gray-300" data-id="element-3415">
              <CheckCircle2 className="w-5 h-5 text-brand-500" data-id="element-3416" />
              <span data-id="element-3417">Guaranteed 3-day payouts</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12" data-id="element-3418">
        <div className="w-full max-w-md" data-id="element-3419">
          <div className="lg:hidden flex items-center justify-between gap-4 mb-8" data-id="element-3420">
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity" aria-label="Back to modules" data-id="element-3421">
              <img src="https://cdn.magicpatterns.com/uploads/kwTMidwDpg46YyVPyEcdAS/Pair2_(2).svg" alt="Pair2" className="h-7 w-auto" data-id="element-3422" />
              <span className="font-display font-bold text-xl text-gray-900" data-id="element-3423">
                Carrier
              </span>
            </Link>
            <PoweredByRTA data-id="element-3424" />
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-3425">
            <div className="w-12 h-12 rounded-2xl bg-gray-900 flex items-center justify-center mb-6 shadow-lg" data-id="element-3426">
              {step === 'phone' ? <Truck className="w-6 h-6 text-white" data-id="element-3427" /> : <Shield className="w-6 h-6 text-white" data-id="element-3428" />}
            </div>

            <h2 className="text-3xl font-display font-bold text-gray-900 mb-2" data-id="element-3429">
              {step === 'phone' ? 'Welcome back' : 'Verify your number'}
            </h2>
            <p className="text-gray-500 mb-8" data-id="element-3430">
              {step === 'phone' ? 'Sign in to manage your fleet and accept jobs.' : `We sent a code to +971 ${phone}`}
            </p>

            {error && <div className="mb-6 p-3 bg-red-50 text-red-700 text-sm font-medium rounded-xl border border-red-100" data-id="element-3431">
                {error}
              </div>}

            {step === 'phone' ? <form onSubmit={handleSendOtp} data-id="element-3432">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block" data-id="element-3433">
                  Mobile number
                </label>
                <div className="flex gap-2 mb-6" data-id="element-3434">
                  <div className="flex items-center gap-2 px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl shrink-0" data-id="element-3435">
                    <span className="text-base" data-id="element-3436">🇦🇪</span>
                    <span className="font-bold text-gray-900" data-id="element-3437">+971</span>
                  </div>
                  <input type="tel" autoFocus value={phone} onChange={e => setPhone(e.target.value)} placeholder="50 123 4567" className="flex-1 px-4 py-3.5 rounded-xl border border-gray-200 outline-none text-lg focus:border-brand-300 font-medium tracking-wide" data-id="element-3438" />
                </div>
                <button type="submit" disabled={!phoneValid} className="w-full py-4 bg-gray-900 hover:bg-black text-white rounded-xl font-bold text-base transition-colors flex items-center justify-center gap-2 disabled:opacity-50" data-id="element-3439">
                  Continue <ArrowRight className="w-5 h-5" data-id="element-3440" />
                </button>
              </form> : <form onSubmit={handleVerifyOtp} data-id="element-3441">
                <div className="flex gap-2 justify-between mb-8" data-id="element-3442">
                  {otp.map((digit, i) => <input key={i} ref={el => inputsRef.current[i] = el} type="text" inputMode="numeric" value={digit} onChange={e => updateOtp(i, e.target.value)} onKeyDown={e => onOtpKey(i, e)} onPaste={i === 0 ? undefined : undefined} className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold rounded-xl border border-gray-200 focus:border-brand-400 focus:ring-2 focus:ring-brand-50 outline-none bg-gray-50 focus:bg-white transition-all" data-id="element-3443" />)}
                </div>
                <button type="submit" className="w-full py-4 bg-gray-900 hover:bg-black text-white rounded-xl font-bold text-base transition-colors flex items-center justify-center gap-2" data-id="element-3444">
                  Verify & Sign In <ArrowRight className="w-5 h-5" data-id="element-3445" />
                </button>
                <p className="text-center mt-6 text-sm text-gray-500" data-id="element-3446">
                  Didn't receive it?{' '}
                  <button type="button" onClick={() => setStep('phone')} className="font-bold text-gray-900 hover:underline" data-id="element-3447">
                    Try again
                  </button>
                </p>
              </form>}

            {step === 'phone' && <div className="mt-8 pt-8 border-t border-gray-100 text-center" data-id="element-3448">
                <p className="text-gray-500" data-id="element-3449">
                  Don't have an account?{' '}
                  <Link to="/supplier/register" className="font-bold text-brand-600 hover:text-brand-700" data-id="element-3450">
                    Register your business →
                  </Link>
                </p>
              </div>}
          </motion.div>
        </div>
      </div>
    </div>;
}